import React from 'react';
import { useDrag, useDragDropManager, useDrop } from 'react-dnd';

type DragItem = {
  index: number;
  id: string;
  type: string;
};

type UseDndReorderParams = {
  type: string;
  index: number;
  name: string;
  onReorderHover?: (dragIndex: number, hoverIndex: number) => void;
  onReorderDrop?: () => void;
  scrollThreshold?: number;
  scrollSpeed?: number;
};

/**
 * Finds the closest scrollable parent of an element
 * @param element The element to find the closest scrolling parent for
 * @returns The closest scrollable parent or document.documentElement as fallback
 */
const getScrollingParent = (element: HTMLElement | null): HTMLElement => {
  if (!element) {
    return document.documentElement;
  }

  // Start with the parent
  let parent = element.parentElement;

  // Loop through ancestors
  while (parent) {
    // Get computed style to check overflow properties
    const style = window.getComputedStyle(parent);
    const overflowY = style.getPropertyValue('overflow-y');
    const overflowX = style.getPropertyValue('overflow-x');

    const hasScrollableOverflow =
      ['auto', 'scroll', 'overlay'].includes(overflowY) ||
      ['auto', 'scroll', 'overlay'].includes(overflowX);

    // Check if element actually has scrollable content
    const canScrollVertically = parent.scrollHeight > parent.clientHeight;
    const canScrollHorizontally = parent.scrollWidth > parent.clientWidth;

    if (
      (hasScrollableOverflow && (canScrollVertically || canScrollHorizontally)) ||
      parent === document.body
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  // If no scrollable parent found, return documentElement as fallback
  return document.documentElement;
};

export const useDndReorder = ({
  type,
  index,
  name,
  onReorderDrop,
  onReorderHover,
  scrollThreshold = 0.2,
  scrollSpeed = 50,
}: UseDndReorderParams) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const dragDropManager = useDragDropManager();
  const [scrollDirection, setScrollDirection] = React.useState<'up' | 'down' | null>(null);
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const lastTimeRef = React.useRef<number>(0);

  const getScrollContainer = React.useCallback(() => {
    if (scrollContainerRef.current) {
      return scrollContainerRef.current;
    }

    const container = getScrollingParent(ref.current);
    scrollContainerRef.current = container;
    return container;
  }, []);

  const [, drop] = useDrop({
    accept: type,
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const mousePosition = monitor.getClientOffset() ?? { x: 0, y: 0 };
      const hoverClientY = mousePosition.y - hoverBoundingRect.top;

      // NOTE: If it's the same item, don't move it.
      if (dragIndex === hoverIndex) {
        return;
      }

      // NOTE: We want to move items only when the mouse cursor crosses half of the target item.
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onReorderHover?.(dragIndex, hoverIndex);

      // NOTE: We do it to avoid flickering issue.
      item.index = hoverIndex;
    },
  });

  const [{ opacity }, drag] = useDrag({
    type,
    item: () => {
      return {
        index,
        itemName: name,
      };
    },
    collect: (monitor) => {
      return {
        opacity: monitor.isDragging() ? 0.5 : 1,
      };
    },
    end: () => {
      onReorderDrop?.();
    },
  });

  drag(drop(ref));

  // Handle scrolling logic
  React.useEffect(() => {
    // Subscribe to drag drop state
    const unsubscribe = dragDropManager.getMonitor().subscribeToStateChange(() => {
      const monitor = dragDropManager.getMonitor();

      if (monitor.isDragging()) {
        const clientOffset = monitor.getClientOffset();

        if (!clientOffset || !ref.current) {
          return;
        }

        // Get the scrolling container each time to ensure it's up to date
        const container = getScrollContainer();
        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;
        const containerHeight = containerRect.height;

        // Calculate threshold in pixels based on the fraction and container height
        const thresholdPixels = containerHeight * scrollThreshold;

        // Check if cursor is near the top edge
        if (clientOffset.y - containerTop < thresholdPixels) {
          setScrollDirection('up');
        }
        // Check if cursor is near the bottom edge
        else if (containerBottom - clientOffset.y < thresholdPixels) {
          setScrollDirection('down');
        } else {
          setScrollDirection(null);
        }
      } else {
        setScrollDirection(null);
      }
    });

    return () => unsubscribe();
  }, [dragDropManager, scrollThreshold, getScrollContainer]);

  // Handle auto-scrolling using requestAnimationFrame
  React.useEffect(() => {
    if (!scrollDirection || !ref.current) {
      return;
    }

    const scrollStep = (timestamp: number) => {
      const container = getScrollContainer();

      // Calculate time delta in seconds for consistent scrolling speed
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = (timestamp - lastTimeRef.current) / 1000; // Convert to seconds
      lastTimeRef.current = timestamp;

      // Calculate pixels to scroll based on time and desired pixels per second
      const pixelsToScroll = deltaTime * scrollSpeed;

      if (scrollDirection === 'up') {
        container.scrollBy(0, -pixelsToScroll);
      } else if (scrollDirection === 'down') {
        container.scrollBy(0, pixelsToScroll);
      }

      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(scrollStep);
    };

    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(scrollStep);

    // Cleanup function
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      lastTimeRef.current = 0;
    };
  }, [scrollDirection, getScrollContainer, scrollSpeed]);

  return {
    ref: onReorderHover && onReorderDrop ? ref : undefined,
    opacity: onReorderHover && onReorderDrop ? opacity : undefined,
  };
};
